import { z } from 'zod';
import { Constants } from '@/lib/api/database/types';

export const zNotificationTypeEnum = z.enum(
    Constants.public.Enums.notification_type,
);
export type NotificationTypeEnum = z.infer<typeof zNotificationTypeEnum>;
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace NotificationTypeEnum {
    export const DATAROOM_VISIT = 'dataroom_visit';
}

import { InferSelectResult } from '@/lib/api/database/postgrest.helpers';

export const NotificationQuery =
    '*, dataroom_visit(*, visitor:users!visitor_id(*))';
export type NotificationRowType = InferSelectResult<
    'notifications',
    typeof NotificationQuery
>;

export const NotificationRelatedVisitSchema = z.object({
    id: z.string().uuid(),
    viewer_display_name: z.string(),
    viewer_email: z.string().optional().nullable(),
    viewer_id: z.string().uuid().nullable(),
});
const NotificationBaseSchema = z.strictObject({
    id: z.string().uuid(),
    type: zNotificationTypeEnum,
    bumped_at: z.coerce.date().transform((val) => val.toISOString()),
    viewed_at: z.union([
        z.null(),
        z.coerce.date().transform((val) => val.toISOString()),
    ]),
    // TODO custom payload
});
export const NotificationDataroomVisitSchema = NotificationBaseSchema.extend({
    type: z.literal('dataroom_visit'),
    related_visit: NotificationRelatedVisitSchema,
});
export const NotificationCustomSchema = NotificationBaseSchema.extend({
    type: z.literal('custom'),
});
export const NotificationViewedSchema = z
    .object({
        type: z.literal('notification-viewed'),
        id: z.string().uuid(),
        viewed_at: z.union([
            z.null(),
            z.coerce.date().transform((val) => val.toISOString()),
        ]),
    })
    .passthrough();
export const NotificationSchema = z.discriminatedUnion('type', [
    NotificationDataroomVisitSchema,
    NotificationCustomSchema,
    NotificationViewedSchema,
]);
export type NotificationDto = z.infer<typeof NotificationSchema>;

export function mapNotification(
    notification: NotificationRowType,
    hasSub: boolean,
): NotificationDto | null {
    const base = {
        id: notification.id,
        bumped_at: notification.bumped_at,
        viewed_at: notification.viewed_at,
        type: notification.type,
    };
    switch (notification.type) {
        case NotificationTypeEnum.DATAROOM_VISIT:
            return {
                ...base,
                related_visit: hasSub
                    ? {
                          id: notification.dataroom_visit!.id,
                          viewer_display_name:
                              notification.dataroom_visit!.visitor.name ??
                              notification.dataroom_visit!.visitor.email,
                          viewer_email:
                              notification.dataroom_visit!.visitor.email,
                          viewer_id: notification.dataroom_visit!.visitor.id,
                      }
                    : {
                          id: notification.dataroom_visit!.id,
                          viewer_display_name: 'An Investor',
                          viewer_id: null,
                      },
            };
        default:
            console.error(
                `Don't know how to serialize notification [${notification.id}] of type [${notification.type}]`,
            );
            return null;
    }
}

export const SendWebsocketEventSchema = z.object({
    payload: z.any(), // TODO typing for notifications?
    topic: z.string().nullable().optional(),
    userId: z.string().uuid(),
});
export type SendWebsocketEventDto = z.infer<typeof SendWebsocketEventSchema>;

const WebsocketRequestBaseSchema = z.strictObject({
    rid: z.string().uuid().optional(),
    type: z.string(),
});
export const WebsocketSubscribeRequestSchema =
    WebsocketRequestBaseSchema.extend({
        type: z.literal('subscribe'),
        topic: z.union([z.string(), z.array(z.string()).nonempty()]),
    });
export const WebsocketUnsubscribeRequestSchema =
    WebsocketRequestBaseSchema.extend({
        type: z.literal('unsubscribe'),
        topic: z.union([z.string(), z.array(z.string()).nonempty()]),
    });
export const WebsocketUpdateSessionSchema = WebsocketRequestBaseSchema.extend({
    type: z.literal('update-session'),
    accessToken: z.string(),
});
export const WebsocketRequestSchema = z.discriminatedUnion('type', [
    WebsocketSubscribeRequestSchema,
    WebsocketUnsubscribeRequestSchema,
    WebsocketUpdateSessionSchema,
]);
export type WebsocketRequestDto = z.infer<typeof WebsocketRequestSchema>;

// TODO message not response?
export const WebsocketHelloResponse = WebsocketRequestBaseSchema.extend({
    type: z.literal('hello'),
    userId: z.string().uuid(),
}).passthrough();
export const WebsocketErrorResponse = WebsocketRequestBaseSchema.extend({
    type: z.literal('error'),
    error: z.string().optional(),
}).passthrough();
export const WebsocketEventResponse = WebsocketRequestBaseSchema.extend({
    type: z.literal('event'),
    topic: z.string().nullable().optional(),
    // TODO unused, duplicated. may be useful for idempotency. ignore for now
    id: z.string().uuid().nullable().optional(),
    payload: NotificationSchema,
}).passthrough();
export const WebsocketUnknownResponse = WebsocketRequestBaseSchema.extend({
    type: z.string(),
}).passthrough();
export const WebsocketResponseSchema = z.discriminatedUnion('type', [
    WebsocketHelloResponse,
    WebsocketEventResponse,
    WebsocketErrorResponse,
    // WebsocketUnknownResponse,
]);
export const WebsocketResponseKnownTypes = [
    ...WebsocketResponseSchema.optionsMap.keys(),
];
export type WebsocketResponseDto = z.infer<typeof WebsocketResponseSchema>;

const CreateNotificationBaseSchema = z.strictObject({
    type: zNotificationTypeEnum,
    // TODO custom payload
});
export const CreateNotificationDataroomVisitSchema =
    CreateNotificationBaseSchema.extend({
        type: z.literal('dataroom_visit'),
        related_visit_id: z.string().uuid(),
    });
export const CreateNotificationCustomSchema =
    CreateNotificationBaseSchema.extend({
        type: z.literal('custom'),
        // TODO custom payload
    });
export const CreateNotificationSchema = z.discriminatedUnion('type', [
    CreateNotificationDataroomVisitSchema,
    CreateNotificationCustomSchema,
]);
export type CreateNotificationDto = z.infer<typeof CreateNotificationSchema>;
